import React, { useMemo, useRef, useState } from 'react'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import { getCtaStorage } from '../../utils/cta-storage'
import { CloseButton } from '@local/do-secundo-closebutton'
import { Anchor } from '../Anchor/Anchor'
import { useOOGlobals } from '@local/do-secundo-oo-globals-provider'
import { track } from '@toasttab/do-secundo-analytics'
import styles from './TakeoutCTA.module.css'

const CTA_KEY = 'takeout-cta'
const LINK = 'https://toasttakeout.page.link/oov3'
const DATA_TESTIDS = ['add-to-cart-button', 'cart-checkout-button']

export const TRACK_CLOSED = 'TTO CTA Manually Closed'
export const TRACK_SEEN = 'TTO CTA Observed'
export const TRACK_DISMISSED = 'TTO CTA Automatically Dismissed'
export const TRACK_FOLLOWED = 'TTO CTA Link Clicked'

const querySelectorListener = (listener) => (e) => {
  if (e && e.target && typeof listener === 'function') {
    const target =
      e.target.tagName !== 'button' ? e.target.closest('button') : e.target
    if (
      target &&
      DATA_TESTIDS.indexOf(target.getAttribute('data-testid')) > -1
    ) {
      listener(e)
    }
  }
}

const attachToTargetClick = (listener) => {
  document.addEventListener('click', querySelectorListener(listener))
}

const detachFromTargetClick = (listener) => {
  document.removeEventListener('click', querySelectorListener(listener))
}

export const TakeoutCTA = React.memo(() => {
  const { toastTakeoutCTAEnabled } = useOOGlobals()
  const [hasSeen, setHasSeen] = useState(Boolean(getCtaStorage().get(CTA_KEY)))
  const trackOnceHasSeenRef = useRef(false) // Track that the guest has seen the CTA
  const trackOnceHasDismissed = useRef(false) // Dismissed by clicking anywhere
  const trackOnceHasClosed = useRef(false) // Explicitly closed the CTA
  const trackOnceHasFollowed = useRef(false) // Followed the CTA link

  const dismiss = (reason) => (e) => {
    try {
      detachFromTargetClick(dismiss)
      getCtaStorage().set(CTA_KEY, true)
      setHasSeen(true)
      switch (reason) {
        case TRACK_CLOSED:
          if (!trackOnceHasClosed.current) {
            trackOnceHasClosed.current = true
            track(TRACK_CLOSED)
          }
          break
        case TRACK_FOLLOWED:
          if (!trackOnceHasFollowed.current) {
            trackOnceHasFollowed.current = true
            track(TRACK_FOLLOWED)
          }
          break
        default:
          if (!trackOnceHasDismissed.current) {
            trackOnceHasDismissed.current = true
            track(TRACK_DISMISSED)
          }
      }
    } catch (err) {
      // Being cautious because we're attaching to a global listener
    }
  }

  const defaultDismiss = dismiss()

  React.useEffect(() => {
    detachFromTargetClick(defaultDismiss)
    if (toastTakeoutCTAEnabled && !hasSeen) {
      if (!hasSeen) {
        attachToTargetClick(defaultDismiss)
        if (!trackOnceHasSeenRef.current) {
          trackOnceHasSeenRef.current = true
          track(TRACK_SEEN)
        }
      }
    }
    return () => {
      detachFromTargetClick(defaultDismiss)
    }
  }, [hasSeen, toastTakeoutCTAEnabled])

  if (!toastTakeoutCTAEnabled) {
    detachFromTargetClick(defaultDismiss)
    return null
  }

  const dismissClosed = dismiss(TRACK_CLOSED)
  const dismissFollowed = dismiss(TRACK_FOLLOWED)

  return (
    <div data-testid='takeout-cta'>
      <TransitionGroup component={null}>
        {hasSeen === false && (
          <CSSTransition classNames={{ ...styles }} timeout={250}>
            <div
              className={styles.takeoutCta}
              data-testid='takeout-cta-content'
            >
              <div className={styles.closeButton}>
                <CloseButton
                  onClick={dismissClosed}
                  data-testid='close-button-takeout-cta'
                  to=''
                />
              </div>
              <p className={styles.header}>
                Order now on the Toast Takeout app!
              </p>
              <Anchor
                data-testid='cta-link'
                rel='noopener'
                href={LINK}
                target='_blank'
                onClick={dismissFollowed}
              >
                <div className={styles.content} />
              </Anchor>
            </div>
          </CSSTransition>
        )}
      </TransitionGroup>
    </div>
  )
})
